import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  InputLabel,
  Checkbox,
  FormControlLabel,
  TextField,
  Tabs,
  Tab,
  Box
} from "@mui/material";

// The old Settings -> Organisation page, now the third tab ("Configurables").
// Rendered embedded, so it has no heading or Save button of its own and is
// committed by this dialog's "Save changes".
import OrgSettings from "./OrgSettings";

// The staff/manager notification settings — the settings-shaped part of
// Communications. The full Communications hub (which now also holds the Action
// centre) is its own page; this tab is the configuration slice of it, embedded
// exactly like OrgSettings above with no heading or Save button of its own.
import { StaffNotificationSettings } from "./Communications";

// Shared with SiteManagement.js so the admin view and the ordinary user's
// dialog render exactly the same controls.
import {
  defaultOpeningTimes,
  defaultPatientAppSettings,
  panelStyle,
  panelHeaderStyle,
  reportListStyle,
  ReportRow,
  OrganisationIdentityPanel,
  OrganisationReportsPanel,
  BranchSettingsPanel,
} from "./OrgSettingsPanels";

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
      // Explicit display, not just the `hidden` attribute: the panels are all
      // mounted now, and app CSS could otherwise re-assert `display: block`.
      style={{ display: value === index ? undefined : "none" }}
    >
      {/* Panels stay MOUNTED (rather than `value === index && ...`) so the
          Configurables tab keeps its loaded values and its ref stays live
          when you're on another tab — otherwise switching tabs would discard
          unsaved edits and Save changes couldn't reach it. */}
      <Box sx={{ pt: 2 }}>{children}</Box>
    </div>
  );
};

const Settings = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const orgSettingsRef = useRef(null);   // Configurables tab (embedded OrgSettings)
  const communicationsRef = useRef(null); // Communications tab (embedded Communications)
  const [groupData, setGroupData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [checkboxes, setCheckboxes] = useState({
    scanToShelf: true,
    inventory: true,
    housekeeping: true,
    activities: true,
    communications: true,
    report_sim: false,
    report_expiring: false,
    report_slow: false,
    report_fridge: false,
    send_timesheets: false,
    send_expired: false,
    // Branch managers panel
    branch_sms: false,
    branch_expiring: false,
    branch_slowmovers: false,
    branch_fridge: false,
    branch_expired: false,
    branch_timesheets: false,
  });

  const [timesheetDay, setTimesheetDay] = useState(1);
  // Payroll month end: "last" (default) | "first" | "1".."31". The timesheet
  // payroll period for a month runs BACKWARDS from this date; days after the
  // generation date are estimated at scheduled hours.
  const [payrollMonthEnd, setPayrollMonthEnd] = useState("last");

  // Monthly site performance review (branch managers) — stored separately in
  // pharmace_common_db.monthly_report_settings via /api/monthlyreport/settings,
  // NOT through updateGroupSettings (different store; the runner reads it).
  // Loaded from the server on open; saved by the same Save button, but only
  // if the user actually toggled it this session (dirty flag), so a failed
  // load can never silently overwrite the stored value with a default.
  const [monthlyReviewEnabled, setMonthlyReviewEnabled] = useState(false);
  const [monthlyReviewDirty, setMonthlyReviewDirty] = useState(false);

  // Monthly locum payment schedule email (rota) — stored in
  // pharmace_common_db.rota_settings via /api/headoffice/rota/settings,
  // the same setting as 'Email schedule monthly' on the Pharmacist rota
  // page. Same server-truth + dirty-flag pattern as the review row above.
  const [rotaLocumPayEnabled, setRotaLocumPayEnabled] = useState(false);
  const [rotaLocumPayDirty, setRotaLocumPayDirty] = useState(false);

  const [openingTimes, setOpeningTimes] = useState(defaultOpeningTimes);
  const [selectedPharmacyIndex, setSelectedPharmacyIndex] = useState(0);
  const [managerEmail, setManagerEmail] = useState("");
  const [ocsCode, setOcsCode] = useState("");
  // Retained for internal bookkeeping only. The per-branch contact email field
  // was removed from the UI: branch reports are sent to the manager-role staff
  // found in each branch's own database, not to a value entered here.
  const [memberEmail, setMemberEmail] = useState("");

  // Appraisal widget keys (branch-embeddable appraisal system). Keys are
  // listed/created/revoked live against the API — independent of the
  // dialog's Save button. The full key is shown ONCE at creation.
  const [widgetKeys, setWidgetKeys] = useState([]);        // [{keyTail,pharmacyName,label,allowedOrigins,enabled,createdAt,lastUsedAt}]
  const [widgetKeyBusy, setWidgetKeyBusy] = useState(false);
  const [newWidgetKey, setNewWidgetKey] = useState(null);  // { pharmacyName, key } — one-time display

  const widgetAuthHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
  });

  const loadWidgetKeys = async () => {
    try {
      const res = await fetch(`https://api.pharmacegroup.co.uk/api/appraisals/widget-keys/list`, {
        method: "POST", headers: widgetAuthHeaders(), body: "{}"
      });
      const d = await res.json();
      if (res.ok) setWidgetKeys(d.keys || []);
    } catch (_) {}
  };

  const generateWidgetKey = async (pharmacyName) => {
    const origin = window.prompt(
      `Generate an appraisal widget key for "${pharmacyName}".\n\nOptionally restrict it to the branch website's address (recommended) — e.g. https://branch-site.co.uk — or leave blank to allow any site:`,
      "");
    if (origin === null) return;   // cancelled
    setWidgetKeyBusy(true);
    try {
      const res = await fetch(`https://api.pharmacegroup.co.uk/api/appraisals/widget-keys/create`, {
        method: "POST", headers: widgetAuthHeaders(),
        body: JSON.stringify({
          pharmacyName,
          label: "Branch website (issued from Settings)",
          allowedOrigins: origin.trim() ? [origin.trim()] : [],
          issuedBy: { name: localStorage.getItem("userName") || "", roleKey: localStorage.getItem("roleKey") || "GROUP_MANAGER" }
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setNewWidgetKey({ pharmacyName, key: d.key });
      loadWidgetKeys();
    } catch (e) { window.alert(`Could not generate key: ${e.message}`); }
    finally { setWidgetKeyBusy(false); }
  };

  const revokeWidgetKey = async (k) => {
    if (!window.confirm(`Revoke the appraisal widget key ${k.keyTail} for ${k.pharmacyName}?\n\nThe branch website using it loses access on its next request.`)) return;
    setWidgetKeyBusy(true);
    try {
      const res = await fetch(`https://api.pharmacegroup.co.uk/api/appraisals/widget-keys/revoke`, {
        method: "POST", headers: widgetAuthHeaders(),
        body: JSON.stringify({ keyTail: k.keyTail.replace(/^…/, "") })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      loadWidgetKeys();
    } catch (e) { window.alert(`Could not revoke: ${e.message}`); }
    finally { setWidgetKeyBusy(false); }
  };
  const [patientAppSettings, setPatientAppSettings] = useState({ ...defaultPatientAppSettings });
  // Organisation identity + login credentials (Settings -> Organisation).
  // The server is the source of truth: password is never held in localStorage,
  // so these are fetched from /api/organisation/credentials when the dialog
  // opens and saved back only when something has actually been edited.
  const [orgName, setOrgName] = useState("");
  const [orgUsername, setOrgUsername] = useState("");
  const [orgPassword, setOrgPassword] = useState("");
  const [orgCredsLoaded, setOrgCredsLoaded] = useState(false);
  const [orgCredsDirty, setOrgCredsDirty] = useState(false);
  const [showOrgPassword, setShowOrgPassword] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPharmacyName, setNewPharmacyName] = useState("");
  const [newPharmacyCode, setNewPharmacyCode] = useState("");
  const [addError, setAddError] = useState("");

  useEffect(() => {
    if (open) {
      const savedEmail = localStorage.getItem("manager_email") || "";
      setManagerEmail(savedEmail);

      const organisation_name = localStorage.getItem("organisationName") || "";
      const expireBefore = parseInt(localStorage.getItem("expireBefore")) || 0;
      const slowBefore = parseInt(localStorage.getItem("slowBefore")) || 0;
      const members = JSON.parse(localStorage.getItem("members")) || [];

      const parsed = {
        organisation_name,
        expireBefore,
        slowBefore,
        members,
        email: localStorage.getItem("email") || ""
      };

      setGroupData(parsed);
      setEditedData(JSON.parse(JSON.stringify(parsed)));

      const storedCheckboxes = {
        scanToShelf: JSON.parse(localStorage.getItem("scanToShelf") || "true"),
        inventory: JSON.parse(localStorage.getItem("inventory") || "true"),
        housekeeping: JSON.parse(localStorage.getItem("housekeeping") || "true"),
        activities: JSON.parse(localStorage.getItem("activities") || "true"),
        communications: JSON.parse(localStorage.getItem("communications") || "true"),
        report_sim: JSON.parse(localStorage.getItem("report_sim") || "false"),
        report_expiring: JSON.parse(localStorage.getItem("report_expiring") || "false"),
        report_slow: JSON.parse(localStorage.getItem("report_slow") || "false"),
        report_fridge: JSON.parse(localStorage.getItem("report_fridge") || "false"),
        send_timesheets: JSON.parse(localStorage.getItem("send_timesheets") || "false"),
        send_expired: JSON.parse(localStorage.getItem("send_expired") || "false"),
        branch_sms: JSON.parse(localStorage.getItem("branch_sms") || "false"),
        branch_expiring: JSON.parse(localStorage.getItem("branch_expiring") || "false"),
        branch_slowmovers: JSON.parse(localStorage.getItem("branch_slowmovers") || "false"),
        branch_fridge: JSON.parse(localStorage.getItem("branch_fridge") || "false"),
        branch_expired: JSON.parse(localStorage.getItem("branch_expired") || "false"),
        branch_timesheets: JSON.parse(localStorage.getItem("branch_timesheets") || "false"),
      };

      setCheckboxes(storedCheckboxes);

      const storedDay = parseInt(localStorage.getItem("timesheet_day")) || 1;
      setTimesheetDay(storedDay);
      setPayrollMonthEnd(localStorage.getItem("payroll_month_end") || "last");

      // Monthly site performance review — the server is the source of truth
      // (no localStorage cache). On load failure the row simply shows
      // unchecked; because saves are gated on the dirty flag, an untouched
      // row never writes anything back.
      setMonthlyReviewDirty(false);
      (async () => {
        try {
          const token = localStorage.getItem("authToken");
          const res = await fetch(`https://api.pharmacegroup.co.uk/api/monthlyreport/settings/get`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({})
          });
          const data = await res.json();
          if (res.ok) setMonthlyReviewEnabled(data.enabled === true);
          else console.error("monthly review setting load failed:", data.error || res.status);
        } catch (e) {
          console.error("monthly review setting load failed:", e);
        }
      })();

      // Monthly locum payment email — rota settings are the source of truth.
      setRotaLocumPayDirty(false);
      (async () => {
        try {
          const token = localStorage.getItem("authToken");
          const res = await fetch(`https://api.pharmacegroup.co.uk/api/headoffice/rota/settings`, {
            method: "GET",
            headers: { "Authorization": `Bearer ${token}` }
          });
          const data = await res.json();
          if (res.ok) setRotaLocumPayEnabled(data.settings?.emailLocumPaymentMonthly === true);
          else console.error("rota locum payment setting load failed:", data.error || res.status);
        } catch (e) {
          console.error("rota locum payment setting load failed:", e);
        }
      })();

      if (members.length > 0) {
        const first = members[0];
        const firstTimes = first.openingTimes || defaultOpeningTimes;
        setOpeningTimes(firstTimes);
        setOcsCode(first.ocsCode || "");
        setMemberEmail(first.email || "");

        // Load patient app settings from first pharmacy
        const patientSettings = first.patientAppSettings || { ...defaultPatientAppSettings };
        setPatientAppSettings(patientSettings);
      } else {
        setOpeningTimes(defaultOpeningTimes);
        setOcsCode("");
        setMemberEmail("");
        setPatientAppSettings({ ...defaultPatientAppSettings });
      }

      setSelectedPharmacyIndex(0);
      setTabValue(0);
      loadWidgetKeys();

      // Organisation identity + credentials — server truth, no localStorage cache.
      setOrgCredsDirty(false);
      setOrgCredsLoaded(false);
      setShowOrgPassword(false);
      setOrgName(organisation_name);
      setOrgUsername(localStorage.getItem("email") || "");
      setOrgPassword("");
      (async () => {
        try {
          const token = localStorage.getItem("authToken");
          const res = await fetch(`https://api.pharmacegroup.co.uk/api/organisation/credentials`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          const data = await res.json();
          if (res.ok) {
            setOrgName(data.organisation_name || "");
            setOrgUsername(data.email || "");
            setOrgPassword(data.password || "");
            setOrgCredsLoaded(true);
          } else {
            console.error("organisation credentials load failed:", data.error || res.status);
          }
        } catch (e) {
          console.error("organisation credentials load failed:", e);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The shared panels speak one flat { key: value } object with a single
  // onChange. This dialog keeps the same values spread across several pieces of
  // state, so these two adapters translate between the shapes rather than
  // reshaping state that the save path already depends on.
  const orgPanelValues = {
    ...checkboxes,
    manager_email: managerEmail,
    timesheet_day: timesheetDay,
    payroll_month_end: payrollMonthEnd,
  };

  const handleOrgPanelChange = (key, value) => {
    if (key === "manager_email") setManagerEmail(value);
    else if (key === "timesheet_day") setTimesheetDay(value);
    else if (key === "payroll_month_end") setPayrollMonthEnd(value);
    else setCheckboxes((c) => ({ ...c, [key]: value }));
    setHasChanges(true);
  };

  const handleIdentityChange = (key, value) => {
    if (key === "organisation_name") setOrgName(value);
    else if (key === "email") setOrgUsername(value);
    else if (key === "password") setOrgPassword(value);
    setOrgCredsDirty(true);
    setHasChanges(true);
  };

  // The branch panel wants a members[] entry; this dialog holds the selected
  // branch's fields in three separate pieces of state, so synthesise one.
  const selectedMemberForPanel = {
    ...((editedData?.members || [])[selectedPharmacyIndex] || {}),
    openingTimes,
    ocsCode,
    patientAppSettings,
  };

  const handleBranchChange = (key, value) => {
    if (key === "openingTimes") setOpeningTimes(value);
    else if (key === "ocsCode") setOcsCode(value);
    else if (key === "patientAppSettings") setPatientAppSettings(value);
    setHasChanges(true);
  };

  const handleCheckboxChange = (field) => {
    const updated = { ...checkboxes, [field]: !checkboxes[field] };
    setCheckboxes(updated);
    setHasChanges(true);
  };

  const handleSelectPharmacy = (idx) => {
    setSelectedPharmacyIndex(idx);
    const sel = (editedData?.members || [])[idx];
    setOpeningTimes(sel?.openingTimes || defaultOpeningTimes);
    setOcsCode(sel?.ocsCode || "");
    setMemberEmail(sel?.email || "");
    setPatientAppSettings(sel?.patientAppSettings || { ...defaultPatientAppSettings });
  };

  const inheritOrganisationSettings = () => {
    // TODO: Fetch organisation-level patient app settings and apply to pharmacy
    // For now, this is a placeholder
    alert("Inherit from organisation - API integration pending");
  };

  const addPharmacy = () => {
    const name = (newPharmacyName || "").trim();
    const code = (newPharmacyCode || "").trim();
    if (!name) { setAddError("Pharmacy name is required"); return; }

    const nextMembers = Array.isArray(editedData?.members) ? [...editedData.members] : [];
    nextMembers.push({
      pharmacyName: name,
      pharmacyId: code || name,
      openingTimes: defaultOpeningTimes,
      ocsCode: "",
      email: "",
      patientAppSettings: { ...defaultPatientAppSettings }
    });

    const nextEdited = { ...editedData, members: nextMembers };
    setEditedData(nextEdited);
    setNewPharmacyName("");
    setNewPharmacyCode("");
    setAddError("");
    setAddDialogOpen(false);
    setSelectedPharmacyIndex(nextMembers.length - 1);
    setOpeningTimes(defaultOpeningTimes);
    setMemberEmail("");
    setPatientAppSettings({ ...defaultPatientAppSettings });
    setHasChanges(true);
  };

  const deleteSelectedPharmacy = () => {
    const list = Array.isArray(editedData?.members) ? [...editedData.members] : [];
    if (!list.length) return;
    if (!window.confirm("Remove this pharmacy from the group?")) return;

    list.splice(selectedPharmacyIndex, 1);
    const nextEdited = { ...editedData, members: list };
    setEditedData(nextEdited);

    const nextIndex = Math.max(0, selectedPharmacyIndex - 1);
    setSelectedPharmacyIndex(nextIndex);
    const sel = list[nextIndex];
    setOpeningTimes(sel?.openingTimes || defaultOpeningTimes);
    setOcsCode(sel?.ocsCode || "");
    setMemberEmail(sel?.email || "");
    setPatientAppSettings(sel?.patientAppSettings || { ...defaultPatientAppSettings });
    setHasChanges(true);
  };

  const handleSave = async () => {
    const updated = { ...editedData };
    if (Array.isArray(updated.members) && updated.members.length > 0) {
      updated.members[selectedPharmacyIndex] = {
        ...(updated.members[selectedPharmacyIndex] || {}),
        openingTimes: openingTimes,
        ocsCode: ocsCode,
        patientAppSettings: patientAppSettings
      };
    }

    const dataToSave = {
      ...updated,
      // Keep the group-settings write in step with any rename made on the
      // Organisation tab, so the two calls can't disagree about the name.
      organisation_name: orgCredsLoaded ? orgName : updated.organisation_name,
      ...checkboxes,
      timesheet_day: timesheetDay,
      payroll_month_end: payrollMonthEnd,
      _id: localStorage.getItem("userId"),
      email: groupData?.email || "",
      manager_email: managerEmail,
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.pharmacegroup.co.uk/updateGroupSettings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        console.error("Failed to update organisation settings");
        return;
      }

      localStorage.setItem("manager_email", managerEmail);
      localStorage.setItem("organisationName", dataToSave.organisation_name);
      localStorage.setItem("expireBefore", dataToSave.expireBefore);
      localStorage.setItem("slowBefore", dataToSave.slowBefore);
      localStorage.setItem("members", JSON.stringify(dataToSave.members));

      Object.entries(checkboxes).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });

      localStorage.setItem("timesheet_day", timesheetDay);
      localStorage.setItem("payroll_month_end", String(payrollMonthEnd));

      // Server-truth settings (site performance review + locum payment email)
      // are saved INDEPENDENTLY so one failure cannot silently block the
      // other, and any failure is reported loudly with the HTTP status and
      // server error instead of leaving the dialog open with no feedback.
      const secondaryErrors = [];

      // Organisation name / username / password. Saved only when edited, and
      // only against the caller's own record (the server resolves it from the
      // token). A successful change invalidates the current session.
      let reloginRequired = false;
      if (orgCredsDirty && orgCredsLoaded) {
        try {
          const credRes = await fetch(`https://api.pharmacegroup.co.uk/api/organisation/credentials`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({
              organisation_name: orgName,
              email: orgUsername,
              password: orgPassword
            })
          });
          const credData = await credRes.json().catch(() => ({}));
          if (credRes.ok) {
            reloginRequired = !!credData.reloginRequired && (credData.changed || []).length > 0;
            if ((credData.changed || []).length) {
              localStorage.setItem("organisationName", orgName);
              localStorage.setItem("email", orgUsername);
            }
          } else {
            secondaryErrors.push(`Organisation and login: ${credData.error || `HTTP ${credRes.status}`}`);
          }
        } catch (e) {
          secondaryErrors.push(`Organisation and login: ${e.message}`);
        }
      }

      // Configurables tab (Business Insights financial assumptions). The
      // component no-ops unless it loaded AND was edited, so an untouched tab
      // never writes stale figures back.
      try {
        await orgSettingsRef.current?.save();
      } catch (e) {
        secondaryErrors.push(`Configurables: ${e.message}`);
      }

      // Communications tab. Same contract: no-ops unless loaded and edited.
      try {
        await communicationsRef.current?.save();
      } catch (e) {
        secondaryErrors.push(`Communications: ${e.message}`);
      }

      if (monthlyReviewDirty) {
        try {
          const mrRes = await fetch(`https://api.pharmacegroup.co.uk/api/monthlyreport/settings/save`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ enabled: monthlyReviewEnabled })
          });
          if (mrRes.ok) {
            setMonthlyReviewDirty(false);
          } else {
            const body = await mrRes.text().catch(() => "");
            secondaryErrors.push(`Site performance review: HTTP ${mrRes.status} ${body.slice(0, 200)}`);
          }
        } catch (e) {
          secondaryErrors.push(`Site performance review: ${e.message}`);
        }
      }

      if (rotaLocumPayDirty) {
        try {
          const lpRes = await fetch(`https://api.pharmacegroup.co.uk/api/headoffice/rota/settings/save`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ settings: { emailLocumPaymentMonthly: rotaLocumPayEnabled } })
          });
          if (lpRes.ok) {
            setRotaLocumPayDirty(false);
          } else {
            const body = await lpRes.text().catch(() => "");
            secondaryErrors.push(`Locum payment schedule email: HTTP ${lpRes.status} ${body.slice(0, 200)}`);
          }
        } catch (e) {
          secondaryErrors.push(`Locum payment schedule email: ${e.message}`);
        }
      }

      if (secondaryErrors.length) {
        console.error("Secondary setting save failures:", secondaryErrors);
        window.alert("Some settings could not be saved:\n\n" + secondaryErrors.join("\n\n") +
          "\n\nAll other changes were saved. Please screenshot this message.");
        return;   // keep the dialog open so the failed toggles are not lost
      }

      setGroupData(editedData);
      setHasChanges(false);
      setOrgCredsDirty(false);

      if (reloginRequired) {
        window.alert("Your organisation name, username or password has changed.\n\n" +
          "You'll need to sign in again with the new details.");
      }

      onClose();
    } catch (err) {
      console.error("Error saving settings:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Configuration</DialogTitle>
      <DialogContent>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Organisation Settings" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
          <Tab label="Site Settings" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
          <Tab label="Configurables" id="settings-tab-2" aria-controls="settings-tabpanel-2" />
          <Tab label="Communications" id="settings-tab-3" aria-controls="settings-tabpanel-3" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* ORGANISATION SETTINGS TAB */}
          <div
            style={{
              display: "grid",
              // Single column while the Branch managers panel is hidden.
              // Restore to "1fr 1fr" when re-enabling that panel.
              gridTemplateColumns: "1fr",
              gap: 20,
              alignItems: "start",
            }}
          >
            {/* ORGANISATION IDENTITY + LOGIN — shared with Site management. */}
            <OrganisationIdentityPanel
              organisationName={orgName}
              username={orgUsername}
              password={orgPassword}
              disabled={!orgCredsLoaded}
              showPassword={showOrgPassword}
              onToggleShowPassword={setShowOrgPassword}
              onChange={handleIdentityChange}
              note={
                <>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12, lineHeight: 1.45 }}>
                    The name this organisation is known by across the platform, and the
                    username and password used to sign in. Changing any of these signs
                    you out — sign back in with the new details.
                  </div>
                  {!orgCredsLoaded && (
                    <div style={{ fontSize: 12, color: "#b45309", marginBottom: 8 }}>
                      Loading the current details…
                    </div>
                  )}
                </>
              }
            />

            {/* GROUP MANAGER + SCHEDULED REPORTS — shared with Site management.
                The two toggles below are passed in as extra rows because their
                state lives behind endpoints scoped to the signed-in org, so
                they have no meaning when administering another organisation. */}
            <OrganisationReportsPanel
              values={orgPanelValues}
              onChange={handleOrgPanelChange}
              extraRows={
                <>
                  <ReportRow
                    checked={monthlyReviewEnabled}
                    onChange={() => {
                      setMonthlyReviewEnabled((v) => !v);
                      setMonthlyReviewDirty(true);
                      setHasChanges(true);
                    }}
                    label="Send monthly site performance review (to each branch's managers)"
                  />
                  <div style={{ fontSize: 12, color: "#6b7280", margin: "0 0 4px 30px" }}>
                    Sent automatically on the 1st, covering the previous month. Each site is
                    benchmarked against the other branches of this organisation (peers shown
                    anonymously) and emailed to that site's Manager-role staff. Sites with no
                    manager email on record are skipped.
                  </div>
                  <ReportRow
                    checked={rotaLocumPayEnabled}
                    onChange={() => {
                      setRotaLocumPayEnabled((v) => !v);
                      setRotaLocumPayDirty(true);
                      setHasChanges(true);
                    }}
                    label="Email locum payment schedule to group manager"
                  />
                  <div style={{ fontSize: 12, color: "#6b7280", margin: "0 0 4px 30px" }}>
                    Sent automatically on the 1st, covering the previous month: a tabbed
                    spreadsheet (one tab per locum) of sessions worked, hours on site and
                    amounts due, calculated from each locum's hourly rate in the Pharmacist
                    rota register and each branch's opening hours. Same setting as 'Email
                    schedule monthly' on the Pharmacist rota page.
                  </div>
                </>
              }
            />


            {/* RIGHT PANEL — Branch managers (temporarily hidden — branch
                reports are not yet wired up in the scheduler. Restore this
                block to re-enable the toggles.) */}
            {false && (
            <div style={panelStyle}>
              <div style={panelHeaderStyle}>Branch managers</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                Reports sent to each branch manager. Recipient addresses are taken
                from the branch record.
              </div>

              <div style={reportListStyle}>
                <ReportRow
                  checked={checkboxes.branch_sms}
                  onChange={() => handleCheckboxChange("branch_sms")}
                  label="SIM activity"
                />
                <ReportRow
                  checked={checkboxes.branch_expiring}
                  onChange={() => handleCheckboxChange("branch_expiring")}
                  label="Expiring items"
                />
                <ReportRow
                  checked={checkboxes.branch_slowmovers}
                  onChange={() => handleCheckboxChange("branch_slowmovers")}
                  label="Slow movers"
                />
                <ReportRow
                  checked={checkboxes.branch_fridge}
                  onChange={() => handleCheckboxChange("branch_fridge")}
                  label="Fridge checks"
                />
                <ReportRow
                  checked={checkboxes.branch_expired}
                  onChange={() => handleCheckboxChange("branch_expired")}
                  label="Send monthly removed/expired drugs report"
                />
                <ReportRow
                  checked={checkboxes.branch_timesheets}
                  onChange={() => handleCheckboxChange("branch_timesheets")}
                  label="Send monthly timesheets"
                />
              </div>
            </div>
            )}
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* SITE SETTINGS TAB */}
          <div style={{ display: "flex", flexDirection: "row", gap: 24,
                        alignItems: "flex-start" }}>
            {/* LEFT: Pharmacies list — roughly dialog height, with its OWN
                vertical scrollbar, and sticky so it stays in view while the
                long right-hand panel scrolls. */}
            <div style={{ flex: 0.8, minWidth: 200, display: "flex", flexDirection: "column",
                          position: "sticky", top: 0, alignSelf: "flex-start" }}>
              <h4 style={{ flexShrink: 0 }}>Pharmacies</h4>

              {/* "Add pharmacy" removed — membership is managed centrally in
                  Admin -> Site management. Removal stays available here.
                  The addPharmacy handler and its dialog are left intact below
                  so the button can be restored by re-adding it here. */}
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={deleteSelectedPharmacy}
                  disabled={!editedData?.members?.length}
                >
                  Remove selected
                </Button>
              </div>

              <div style={{ border: "1px solid #e5e7eb", borderRadius: 6,
                            height: "60vh", overflowY: "auto", overflowX: "hidden" }}>
                {(editedData?.members?.length ? editedData.members : [])
                  .map((m, idx) => ({ m, idx }))
                  .sort((a, b) =>
                    (a.m.pharmacyName || a.m.pharmacyId || "").localeCompare(
                      b.m.pharmacyName || b.m.pharmacyId || "", undefined, { sensitivity: "base" }
                    )
                  )
                  .map(({ m, idx }) => (
                    <div
                      key={`${m.pharmacyId || m.pharmacyName || idx}-${idx}`}
                      onClick={() => handleSelectPharmacy(idx)}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                        background: idx === selectedPharmacyIndex ? "#f0f9ff" : "transparent",
                        borderBottom: "1px solid #f3f4f6"
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{m.pharmacyName || m.pharmacyId}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{m.pharmacyId}</div>
                    </div>
                  ))}
                {(!editedData?.members || editedData.members.length === 0) && (
                  <div style={{ padding: 10, color: "#6b7280" }}>No pharmacies yet.</div>
                )}
              </div>
            </div>

            {/* MIDDLE/RIGHT: Details for selected pharmacy */}
            <div style={{ flex: 2 }}>
              {/* Shared with Site management. The appraisal widget key is passed
                  in as a slot rather than living in the shared panel: it is
                  issued against the signed-in org's token, so it only makes
                  sense here and not in the admin view. */}
              <BranchSettingsPanel
                member={selectedMemberForPanel}
                onChange={handleBranchChange}
                onInheritPatientApp={inheritOrganisationSettings}
                extraAfterOcs={
                  <>
                {/* Appraisal widget key — per-pharmacy, live (not part of Save) */}
                {(() => {
                  const selName = (editedData?.members || [])[selectedPharmacyIndex]?.pharmacyName || "";
                  const activeKeys = widgetKeys.filter(k => k.pharmacyName === selName && k.enabled);
                  return (
                    <div style={{ marginTop: 20, border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 14px", background: "#fafafa" }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Appraisal widget key</div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8, lineHeight: 1.45 }}>
                        Lets this branch's own website embed the staff appraisal system, scoped to this
                        pharmacy only. The key is shown once at creation; revoking cuts the branch site's
                        access on its next request. Changes here apply immediately (no Save needed).
                      </div>
                      {activeKeys.length === 0 && (
                        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>No active key for this pharmacy.</div>
                      )}
                      {activeKeys.map(k => (
                        <div key={k.keyTail} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, padding: "5px 0", borderBottom: "1px solid #f3f4f6", flexWrap: "wrap" }}>
                          <code style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 8px" }}>wk_{k.keyTail}</code>
                          <span style={{ color: "#6b7280" }}>
                            created {k.createdAt ? new Date(k.createdAt).toLocaleDateString("en-GB") : "—"}
                            {k.lastUsedAt ? ` · last used ${new Date(k.lastUsedAt).toLocaleDateString("en-GB")}` : " · never used"}
                            {(k.allowedOrigins || []).length ? ` · locked to ${k.allowedOrigins.join(", ")}` : " · any website"}
                          </span>
                          <span
                            onClick={() => !widgetKeyBusy && revokeWidgetKey(k)}
                            style={{ color: "#dc2626", cursor: "pointer", textDecoration: "underline", fontWeight: 600, marginLeft: "auto" }}>
                            Revoke key
                          </span>
                        </div>
                      ))}
                      <span
                        onClick={() => !widgetKeyBusy && selName && generateWidgetKey(selName)}
                        style={{ display: "inline-block", marginTop: 8, color: "#0f766e", cursor: "pointer", textDecoration: "underline", fontWeight: 600, fontSize: 13 }}>
                        {widgetKeyBusy ? "Working…" : activeKeys.length ? "+ Generate an additional key" : "+ Generate widget key for this pharmacy"}
                      </span>
                    </div>
                  );
                })()}
                  </>
                }
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* CONFIGURABLES TAB — the former Settings -> Organisation page. */}
          <OrgSettings
            ref={orgSettingsRef}
            embedded
            onDirty={() => setHasChanges(true)}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* COMMUNICATIONS TAB — automatic staff/manager notifications. */}
          <StaffNotificationSettings
            ref={communicationsRef}
            embedded
            onDirty={() => setHasChanges(true)}
          />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={!hasChanges} variant="contained">
          Save changes
        </Button>
      </DialogActions>

      {/* One-time widget key display */}
      {newWidgetKey && (
        <Dialog open onClose={() => setNewWidgetKey(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Appraisal widget key — {newWidgetKey.pharmacyName}</DialogTitle>
          <DialogContent>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 10, lineHeight: 1.5 }}>
              Copy this key now — <b>it is shown only once</b>. Paste it into the branch website:
              <code style={{ display: "block", background: "#f3f4f6", borderRadius: 4, padding: "6px 10px", margin: "6px 0" }}>
                {'<AppraisalWidget widgetKey="…" />'}
              </code>
            </div>
            <Input fullWidth readOnly value={newWidgetKey.key} onFocus={(e) => e.target.select()} />
            <Button variant="outlined" size="small" style={{ marginTop: 8 }}
              onClick={() => { navigator.clipboard?.writeText(newWidgetKey.key); window.alert("Key copied to clipboard."); }}>
              Copy to clipboard
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewWidgetKey(null)} variant="contained">Done — I've copied it</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add pharmacy dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add pharmacy</DialogTitle>
        <DialogContent>
          <InputLabel>Name</InputLabel>
          <Input fullWidth value={newPharmacyName} onChange={(e) => setNewPharmacyName(e.target.value)} />
          <InputLabel sx={{ mt: 2 }}>Code / ID (optional)</InputLabel>
          <Input fullWidth value={newPharmacyCode} onChange={(e) => setNewPharmacyCode(e.target.value)} />
          {addError && <div style={{ color: "crimson", marginTop: 8 }}>{addError}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={addPharmacy} disabled={!newPharmacyName.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default Settings;